import React, { useEffect, useState } from 'react';
import {
  ARTICLE_MAP,
  ArticleKey,
  DEFAULT_ARTICLE_KEYS,
} from './auth/welcome-screen-articles';
import { Button } from '@/components/button';
import { CheckboxListItem } from '@/components/settings/checkbox-list-item';
import { ColorInputListItem } from '@/components/settings/color-input-list-item';
import { CONTAINER_MODAL_ROUTES } from '@/navigators/routes';
import { ContainerModalStackParams } from '@/navigators/types';
import { createStyles } from '@/contexts/theme-context';
import {
  DemoSettings,
  useDemoSettingsContext,
} from '@/contexts/demo-settings-context';
import { ImageInputListItem } from '@/components/settings/image-input-list-item';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import { ModalContainer } from '@/components/modal-container';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { SectionList, SectionListData, Text, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { TextInputListItem } from '@/components/settings/text-input-list-item';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type SettingsListOption = Exclude<ListOption, 'value'> & {
  value: keyof DemoSettings;
};

const settingsListSections: SectionListData<SettingsListOption>[] = [
  {
    data: [
      { title: 'Accent color', value: 'accentColor' },
      {
        title: 'Accent text color',
        value: 'accentTextColor',
      },
      {
        subtitle: '1024x500',
        title: 'In-app logo',
        value: 'welcomeLogoUrl',
      },
    ],
    key: 'Theme',
  },
  {
    data: [
      {
        title: 'Logo',
        value: 'landingPageLogo',
      },
      {
        title: 'Tagline',
        value: 'landingPageTagline',
      },
      {
        title: 'Articles',
        value: 'landingPageArticles',
      },
    ],
    key: 'Landing page',
  },
];

const landingPageArticleOptions = Object.entries(ARTICLE_MAP).map(
  ([articleKey, article]) => ({
    title: article.name,
    value: articleKey,
  }),
);

type Props = StackScreenProps<
  ContainerModalStackParams,
  CONTAINER_MODAL_ROUTES.DemoSettings
>;

export const DemoSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const { bottom } = useSafeAreaInsets();
  const { demoSettings, setDemoSettings } = useDemoSettingsContext();
  const [styles, theme] = useStyles();

  const [accentColor, setAccentColor] = useState<string>();
  const [accentTextColor, setAccentTextColor] = useState<string>();
  const [landingPageArticles, setLandingPageArticles] =
    useState<ArticleKey[]>();
  const [landingPageLogo, setLandingPageLogo] = useState<string>();
  const [landingPageTagline, setLandingPageTagline] = useState<string>();
  const [welcomeLogoUrl, setWelcomeLogoUrl] = useState<string>();

  useEffect(() => {
    setAccentColor(demoSettings?.accentColor ?? theme.colors.accent);
    setAccentTextColor(
      demoSettings?.accentTextColor ?? theme.colors.accentText,
    );
    setLandingPageArticles(demoSettings?.landingPageArticles);
    setLandingPageLogo(
      demoSettings?.landingPageLogo ?? theme.images.welcomeLogoUrl,
    );
    setLandingPageTagline(demoSettings?.landingPageTagline);
    setWelcomeLogoUrl(
      demoSettings?.welcomeLogoUrl ?? theme.images.welcomeLogoUrl,
    );
  }, [demoSettings, theme]);

  return (
    <ModalContainer
      navigationHeaderProps={{
        title: 'Demo Settings',
      }}
      shouldDismissKeyboard
    >
      <SectionList
        alwaysBounceVertical={false}
        contentInsetAdjustmentBehavior="automatic"
        ItemSeparatorComponent={ListItemSeparator}
        renderItem={({ item }) => {
          switch (item.value) {
            case 'accentColor':
              return (
                <ColorInputListItem
                  item={item}
                  onChange={setAccentColor}
                  value={accentColor}
                />
              );
            case 'accentTextColor':
              return (
                <ColorInputListItem
                  item={item}
                  onChange={setAccentTextColor}
                  value={accentTextColor}
                />
              );
            case 'landingPageArticles':
              return (
                <CheckboxListItem
                  item={item}
                  onChange={setLandingPageArticles}
                  options={landingPageArticleOptions}
                  selectedValues={landingPageArticles ?? DEFAULT_ARTICLE_KEYS}
                />
              );
            case 'landingPageTagline':
              return (
                <TextInputListItem
                  item={item}
                  onChange={setLandingPageTagline}
                  value={landingPageTagline}
                />
              );
            case 'landingPageLogo':
              return (
                <ImageInputListItem
                  alt=""
                  item={item}
                  onChange={setLandingPageLogo}
                  value={landingPageLogo}
                />
              );
            case 'welcomeLogoUrl':
              return (
                <ImageInputListItem
                  alt=""
                  item={item}
                  onChange={setWelcomeLogoUrl}
                  value={welcomeLogoUrl}
                />
              );
            default:
              return null;
          }
        }}
        renderSectionHeader={({ section }) => (
          <View style={styles.sectionHeader}>
            <Text style={[TYPOGRAPHY.listTitle, styles.sectionHeaderText]}>
              {section.key}
            </Text>
          </View>
        )}
        sections={settingsListSections}
      />
      <View
        style={[
          styles.footer,
          { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
        ]}
      >
        <Button
          onPress={() => {
            setDemoSettings({
              accentColor,
              accentTextColor,
              landingPageArticles,
              landingPageLogo,
              landingPageTagline,
              welcomeLogoUrl,
            });

            navigation.goBack();
          }}
          trackingData={null}
        >
          Save
        </Button>
        <Button
          containerStyle={styles.resetButtonContainer}
          onPress={() => {
            setDemoSettings(null);
            navigation.goBack();
          }}
          trackingData={null}
          type="tertiary"
        >
          Reset to default
        </Button>
      </View>
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  footer: {
    paddingHorizontal: SCREEN_PADDING,
  },
  resetButtonContainer: {
    marginTop: 12,
  },
  sectionHeader: {
    backgroundColor: theme.colors.background,
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: 4,
  },
  sectionHeaderText: {
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
}));
